// Top-level crash handler. Catches uncaught errors from the frame loop,
// promise rejections, and any window.onerror event. Logs a rolling 5
// entries to localStorage (so a returning player can hand over a crash
// trace) and surfaces a modal that lets them reload — instead of staring
// at a frozen scene with the dev tools closed.
//
// Idempotent: install() returns the same handler if called more than
// once. Modal mounts lazily on first crash; further crashes append to
// the trace list.
//
// This is jam-juror insurance: a single throw in main.ts (3000 lines)
// would otherwise white-screen the page.

const STORAGE_KEY = 'smp.crashLog';
const MAX_ENTRIES = 5;

type CrashEntry = {
  ts: number;
  context: string;
  message: string;
  stack: string;
  url: string;
};

let installed = false;
let modalMounted = false;
let modalRoot: HTMLDivElement | null = null;
let traceList: HTMLDivElement | null = null;
// Reentrancy guard. `reportCrash` calls `console.error`, which
// Vite's HMR client hooks to forward logs over a WebSocket. When
// that WS is dead the forwarder throws a "can't access property
// 'send' of undefined" — which becomes ANOTHER unhandledrejection,
// which re-enters our handler, which calls console.error, which
// throws again, stacking the modal with dozens of identical
// Vite-HMR traces. This flag breaks the cascade.
let reportingInProgress = false;

// Circuit-breaker for browser-specific error cascades we haven't
// filtered yet. If the handler is firing more than 3 times per
// 2 s window, something is spamming — suppress the modal for 30 s
// to let the browser settle. Covers unknown future stack-format
// variations (mobile browsers, new Vite versions, etc.) without
// needing a filter update per-browser.
let recentFireTimes: number[] = [];
let suppressedUntil = 0;
const BURST_WINDOW_MS = 2_000;
const BURST_THRESHOLD = 3;
const SUPPRESSION_MS = 30_000;

export type CrashReporter = (err: unknown, context: string) => void;

export function installCrashHandler(): CrashReporter {
  if (installed) return reportCrash;
  installed = true;

  addEventListener('error', (e) => {
    if (isIgnorableError(e.error, e.message, e.filename ?? '')) return;
    reportCrash(e.error ?? new Error(e.message), 'window.error');
  });
  addEventListener('unhandledrejection', (e) => {
    if (isIgnorableError(e.reason, errorMessage(e.reason), errorFilename(e.reason))) {
      // Prevent the default "Uncaught (in promise)" console noise
      // for errors we've chosen to ignore — keeps the console clean
      // so REAL errors aren't drowned out.
      e.preventDefault?.();
      return;
    }
    reportCrash(e.reason ?? new Error('unhandled rejection'), 'unhandledrejection');
  });

  return reportCrash;
}

/**
 * Some errors are expected noise and shouldn't surface the crash
 * modal. Biggest offender: Vite's HMR client occasionally calls
 * `ws.send()` when its WebSocket has been closed (dev server restart,
 * network hiccup) which throws
 *   "can't access property 'send' of undefined"
 * from `@vite/client` paths. Not a game error — just HMR reconnect
 * churn. Filter those out so they don't scare the player during dev.
 */
function isIgnorableError(err: unknown, message: string, filename: string): boolean {
  const msg = String(message ?? '');
  let stack = '';
  try { stack = err instanceof Error ? String(err.stack ?? '') : String(err ?? ''); } catch { /* ignore */ }
  const src = `${filename} ${stack}`;
  // Vite HMR client noise — occurs when the HMR WebSocket dies and
  // Vite's `sendLog` / `sendError` / console-forwarder tries to
  // `ws.send()` on undefined. Several matching patterns for cross-
  // browser stack formats.
  if (src.includes('@vite/client') || src.includes('/@vite/client')) return true;
  if (src.includes('setupForwardConsoleHandler')) return true;
  if (src.includes('sendLog') || src.includes('sendError')) return true;
  // The specific message Firefox produces when `ws.send()` on undef:
  //   "can't access property \"send\" of undefined"
  // Chrome:
  //   "Cannot read properties of undefined (reading 'send')"
  if (
    msg.includes("property \"send\" of undefined") ||
    msg.includes("property 'send' of undefined") ||
    msg.includes("properties of undefined (reading 'send')")
  ) return true;
  // Classic benign browser warning.
  if (msg.includes('ResizeObserver loop')) return true;
  return false;
}

function errorFilename(err: unknown): string {
  // PromiseRejectionEvent reasons don't carry a filename; dig into
  // the stack first line if present.
  if (err instanceof Error && err.stack) {
    const firstLine = err.stack.split('\n')[1] ?? '';
    return firstLine;
  }
  return '';
}

export function reportCrash(err: unknown, context: string): void {
  // Reentrancy guard — see top of file.
  if (reportingInProgress) return;
  reportingInProgress = true;
  try {
    const now = Date.now();
    if (now < suppressedUntil) return;
    const message = errorMessage(err);
    const stack = errorStack(err);
    // Final filter gate.
    if (isIgnorableError(err, message, '')) return;

    // Burst detection — if the handler fires BURST_THRESHOLD times
    // in BURST_WINDOW_MS, suppress further reporting for 30 s. A
    // rejection loop on a dying dev-server WS tends to cascade at
    // tens of events per second; real crashes happen at most once.
    recentFireTimes.push(now);
    recentFireTimes = recentFireTimes.filter((t) => now - t <= BURST_WINDOW_MS);
    if (recentFireTimes.length >= BURST_THRESHOLD) {
      suppressedUntil = now + SUPPRESSION_MS;
      recentFireTimes = [];
      console.log('[crash] burst detected — suppressing for', SUPPRESSION_MS, 'ms');
      return;
    }

    const entry: CrashEntry = {
      ts: now,
      context,
      message,
      stack,
      url: typeof location !== 'undefined' ? location.href : 'n/a',
    };
    // Use console.log instead of console.error — Vite's HMR client
    // only forwards .warn/.error/.info by default, so .log is safe
    // against the ws.send-is-undefined cascade on dev-server restart.
    console.log('[crash]', context, entry.message, entry.stack);
    pushToLog(entry);
    showModal(entry);
  } finally {
    reportingInProgress = false;
  }
}

function pushToLog(entry: CrashEntry): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const log: CrashEntry[] = raw ? JSON.parse(raw) : [];
    log.push(entry);
    while (log.length > MAX_ENTRIES) log.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch { /* localStorage may be denied — never block the modal */ }
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try { return JSON.stringify(err); } catch { return String(err); }
}

function errorStack(err: unknown): string {
  if (err instanceof Error && err.stack) return err.stack.slice(0, 1200);
  return '(no stack)';
}

function showModal(entry: CrashEntry): void {
  if (!modalMounted) mountModal();
  if (!modalRoot || !traceList) return;
  modalRoot.style.display = 'flex';
  // Append the latest trace to the top of the list (newest first).
  const row = document.createElement('div');
  row.style.cssText = `
    margin-bottom: 10px; padding: 8px 10px;
    background: #0f172a; border: 1px solid #475569; border-radius: 6px;
    font: 500 10px ui-monospace, monospace; color: #cbd5e1;
    white-space: pre-wrap; word-break: break-word; max-height: 160px; overflow-y: auto;
  `;
  row.textContent = `[${entry.context}] ${entry.message}\n${entry.stack}`;
  traceList.prepend(row);
}

function mountModal(): void {
  if (modalMounted) return;
  modalMounted = true;
  const root = document.createElement('div');
  root.className = 'ui interactive';
  root.style.cssText = `
    position: fixed; inset: 0; z-index: 99999;
    display: none;
    background: rgba(2,6,23,.92);
    align-items: center; justify-content: center;
    backdrop-filter: blur(4px);
    font: 600 12px ui-monospace, monospace; color: #e5e7eb;
  `;
  const card = document.createElement('div');
  card.style.cssText = `
    background: #1e293b; border: 2px solid #ef4444; border-radius: 14px;
    padding: 22px 24px; width: min(560px, 92vw); max-height: 84vh; overflow-y: auto;
    box-shadow: 0 24px 80px rgba(239,68,68,.35);
  `;
  card.innerHTML = `
    <div style="font: 900 18px ui-monospace; color:#ef4444; letter-spacing: 2px; margin-bottom: 4px;">
      🛑 GAME ERROR
    </div>
    <div style="color:#cbd5e1; font-size: 11px; margin-bottom: 14px;">
      The game hit an unexpected error. Reload to recover. If this keeps happening,
      copy the trace below and report it.
    </div>
    <div data-trace style="margin-bottom: 14px;"></div>
    <div style="display: flex; gap: 8px;">
      <button data-act="reload" style="
        flex: 1; padding: 10px 14px;
        background: #ef4444; border: 0; border-radius: 6px;
        color: #fff; font: 800 12px ui-monospace; letter-spacing: 1px;
        cursor: pointer;
      ">RELOAD</button>
      <button data-act="copy" style="
        flex: 0 0 auto; padding: 10px 14px;
        background: #334155; border: 0; border-radius: 6px;
        color: #e5e7eb; font: 700 11px ui-monospace;
        cursor: pointer;
      ">COPY TRACE</button>
      <button data-act="dismiss" style="
        flex: 0 0 auto; padding: 10px 14px;
        background: transparent; border: 1px solid #475569; border-radius: 6px;
        color: #94a3b8; font: 700 11px ui-monospace;
        cursor: pointer;
      ">DISMISS</button>
    </div>
  `;
  root.appendChild(card);
  document.body.appendChild(root);
  modalRoot = root;
  traceList = card.querySelector('[data-trace]') as HTMLDivElement;

  card.querySelector('[data-act="reload"]')?.addEventListener('click', () => {
    location.reload();
  });
  card.querySelector('[data-act="copy"]')?.addEventListener('click', () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) ?? '[]';
      navigator.clipboard?.writeText(raw);
    } catch { /* clipboard may be denied */ }
  });
  card.querySelector('[data-act="dismiss"]')?.addEventListener('click', () => {
    if (modalRoot) modalRoot.style.display = 'none';
  });
}
